import { z } from "zod";
import { shareLinkSchema } from "./share";
import {
  datePrecisionSchema,
  entryKindSchema,
  entrySchema,
  memberRoleSchema,
  trackSchema,
} from "./timeline";

/**
 * L'export et la suppression (ticket 8, #515 ; spec, sections 3.2 et 7).
 *
 * `GET /api/t/export.json` rend toute la timeline du proprietaire en un seul document : la
 * timeline (date de naissance comprise), ses pistes, ses points avec leurs proches nommes, ses
 * proches, toutes les propositions et l'etat des questions. Le document porte son format et sa
 * version (`format`, `version`) : une version suivante pourra le relire, et ce schema le relit
 * deja (test d'aller-retour).
 *
 * Les noms de champs sont ceux des reponses de l'API (camelCase), pas ceux des colonnes.
 */

/** Le nom du format, ecrit dans chaque export. */
export const EXPORT_FORMAT = "ma-timeline";
/** La version du format. Un champ qui change de sens change de version. */
export const EXPORT_VERSION = 1;

/** Un point exporte : ses proches nommes et la mention « propose par » sont toujours la. */
export const exportedEntrySchema = entrySchema.extend({
  viewers: z.array(z.number().int().positive()),
  proposedByMemberId: z.number().int().positive().nullable(),
});
export type ExportedEntry = z.infer<typeof exportedEntrySchema>;

/** Une piste exportee, avec son etat masque (absent d'un export plus ancien : visible). */
export const exportedTrackSchema = trackSchema.extend({ hidden: z.boolean().default(false) });
export type ExportedTrack = z.infer<typeof exportedTrackSchema>;

/** Une ligne `member`, proprietaire compris. */
export const exportedMemberSchema = z.object({
  id: z.number().int().positive(),
  email: z.string(),
  role: memberRoleSchema,
  invitedAt: z.string(),
  revokedAt: z.string().nullable(),
  hasLoggedIn: z.boolean(),
});
export type ExportedMember = z.infer<typeof exportedMemberSchema>;

/** Une proposition, en attente ou acceptee (une proposition refusee est supprimee). */
export const exportedProposalSchema = z.object({
  id: z.number().int().positive(),
  memberId: z.number().int().positive(),
  trackId: z.number().int().positive(),
  kind: entryKindSchema,
  title: z.string(),
  note: z.string().nullable(),
  startRaw: z.string().nullable(),
  startFrom: z.string().nullable(),
  startTo: z.string().nullable(),
  startPrecision: datePrecisionSchema.nullable(),
  endRaw: z.string().nullable(),
  endFrom: z.string().nullable(),
  endTo: z.string().nullable(),
  endPrecision: datePrecisionSchema.nullable(),
  ongoing: z.boolean(),
  undated: z.boolean(),
  status: z.enum(["pending", "accepted", "refused"]),
  /** Le point ne de la proposition acceptee. */
  entryId: z.number().int().positive().nullable(),
  createdAt: z.string(),
});
export type ExportedProposal = z.infer<typeof exportedProposalSchema>;

/** L'etat d'une question de la banque pour cette timeline. */
export const exportedQuestionSchema = z.object({
  questionId: z.string(),
  status: z.enum(["answered", "skipped", "later"]),
  entryId: z.number().int().positive().nullable(),
  updatedAt: z.string(),
});
export type ExportedQuestion = z.infer<typeof exportedQuestionSchema>;

export const timelineExportSchema = z.object({
  format: z.literal(EXPORT_FORMAT),
  version: z.literal(EXPORT_VERSION),
  exportedAt: z.string(),
  timeline: z.object({
    id: z.number().int().positive(),
    birthDate: z.string().nullable(),
    createdAt: z.string(),
  }),
  tracks: z.array(exportedTrackSchema),
  entries: z.array(exportedEntrySchema),
  members: z.array(exportedMemberSchema),
  proposals: z.array(exportedProposalSchema),
  questions: z.array(exportedQuestionSchema),
  /** Les liens de partage (#588), sans jeton ni hash : la base ne garde que le hash. */
  shareLinks: z.array(shareLinkSchema.omit({ entryCount: true })).default([]),
});
export type TimelineExport = z.infer<typeof timelineExportSchema>;

/**
 * La reponse de `DELETE /api/t/compte` : `timeline` quand le proprietaire a efface sa frise,
 * `access` quand un proche a efface son acces. Le compte lui-meme s'efface ensuite par
 * `DELETE /api/me`, la route du template.
 */
export const accountErasureSchema = z.object({
  deleted: z.enum(["timeline", "access"]),
});
export type AccountErasure = z.infer<typeof accountErasureSchema>;
